export const STATUS_TONE = {
  BOOKING_CREATED: "info",
  PACKAGES_ADDED: "info",
  DOCUMENTS_UPLOADED: "info",
  QUOTE_READY: "warning",
  PAYMENT_COMPLETED: "success",
  SENDER_READY_CONFIRMED: "success",
  SOURCE_VEHICLE_ASSIGNED: "info",
  REACHED_SOURCE_AIRPORT: "info",
  ACCEPTED_BY_AIRLINE: "success",
  ARRIVED_DESTINATION_AIRPORT: "info",
  AWAITING_RECEIVER_READY: "warning",
  RECEIVER_READY_CONFIRMED: "success",
  DESTINATION_VEHICLE_ASSIGNED: "info",
  OUT_FOR_DELIVERY: "info",
  DELIVERED: "success",
  DOCUMENTS_REJECTED: "error",
  CANCELLED: "error",
};

// M1 (City-Based Operations Migration) — final operating model roles.
// City operational roles are bound to exactly one airport.
export const CITY_OPS_ROLES = ["CTRL", "CTOP", "CAP", "RID", "ACCOUNTS"];
// Roles selectable when creating / changing a user (SUPER_ADMIN global + city ops; SENDER self-registers).
export const ASSIGNABLE_ROLES = ["SUPER_ADMIN", "CTRL", "CTOP", "CAP", "RID", "ACCOUNTS"];

export const COMMODITY_CATEGORIES = [
  "GARMENTS","TEXTILES","PRINTED_MATERIAL","MACHINE_PARTS",
  "ELECTRONICS_NON_BATTERY","SAMPLES","EVENT_MATERIAL",
  "MEDICAL_NON_TEMP","DOCUMENTS_BULK","OTHER_BUSINESS_GOODS",
];

export const PACKAGE_TYPES = ["CARTON","BOX","BAG","CRATE","TUBE","BUNDLE","OTHER"];

export const RESTRICTED_FLAGS = [
  "CASH","JEWELLERY","NARCOTICS","FIREARMS","EXPLOSIVES","HAZCHEM",
  "LIVE_ANIMALS","HUMAN_REMAINS","PERISHABLE","TEMP_CONTROLLED",
  "LITHIUM_BATTERY","LIQUIDS","BIOLOGICAL",
];

export function statusTone(s) {
  return STATUS_TONE[s] || "neutral";
}

// Phase 2E.2 — minimal "next action" hint for CTOP (computed from current_status).
// Used in Dashboard list and ShipmentDetail header.
export const NEXT_ACTION_BY_STATUS = {
  BOOKING_CREATED: "Sender: add packages",
  PACKAGES_ADDED: "Sender: upload invoice + photos",
  DOCUMENTS_UPLOADED: "CTOP: review/approve documents",
  DOCUMENTS_REJECTED: "Sender: re-upload corrected documents",
  QUOTE_READY: "Sender: pay quote",
  PAYMENT_COMPLETED: "Sender: Confirm Shipment and Authorise Pickup",
  SENDER_READY_CONFIRMED: "Source ops: assign vehicle, then Rider pickup",
  SOURCE_VEHICLE_ASSIGNED: "Sender: confirm loading",
  REACHED_SOURCE_AIRPORT: "Source Captain: scan + upload AWB, then confirm airline handover",
  ACCEPTED_BY_AIRLINE: "Destination Captain: scan destination receipt + photos",
  ARRIVED_DESTINATION_AIRPORT: "Destination CTOP: call receiver, then Mark Receiver Ready",
  AWAITING_RECEIVER_READY: "Destination CTOP: call receiver, then Mark Receiver Ready",
  RECEIVER_READY_CONFIRMED: "CTOP: assign destination vehicle",
  DESTINATION_VEHICLE_ASSIGNED: "Dest Captain: capture VEHICLE_LOADED; then Mark Out For Delivery",
  OUT_FOR_DELIVERY: "Confirm Goods Received via Delivery OTP (public or staff)",
  DELIVERED: "Done - shipment closed",
  CANCELLED: "-",
};

export function nextAction(status) {
  return NEXT_ACTION_BY_STATUS[status] || "-";
}

// P3 — sender-facing public status. Never leaks raw internal enum values. Uses the commercial-phase
// fields (payment/insurance/ack/doc-gen) where present; falls back to a friendly operational label.
export const SENDER_PUBLIC_MILESTONE = {
  SENDER_READY_CONFIRMED: "Preparing for pickup",
  SOURCE_VEHICLE_ASSIGNED: "Pickup scheduled",
  REACHED_SOURCE_AIRPORT: "At source airport",
  ACCEPTED_BY_AIRLINE: "Accepted by airline",
  ARRIVED_DESTINATION_AIRPORT: "Arrived at destination",
  AWAITING_RECEIVER_READY: "Awaiting receiver readiness",
  RECEIVER_READY_CONFIRMED: "Out for delivery soon",
  DESTINATION_VEHICLE_ASSIGNED: "Out for delivery soon",
  OUT_FOR_DELIVERY: "Out for delivery",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
};

const PRE_READY_STATUSES = [
  "BOOKING_CREATED", "PACKAGES_ADDED", "DOCUMENTS_UPLOADED", "QUOTE_READY",
  "DOCUMENTS_REJECTED", "PAYMENT_COMPLETED",
];

export function senderPublicStatus(s) {
  const st = s.current_status;
  if (st === "CANCELLED") return "Cancelled";
  const paid = s.air12_payment_status === "COMPLETED";
  const insConfirmed = (s.insurance_status || "PENDING") === "CONFIRMED";
  const preReady = PRE_READY_STATUSES.includes(st);
  if (!paid && preReady) return "Draft — Awaiting Payment";
  if (paid && !insConfirmed && preReady) return "Booking Confirmed — Insurance Processing";
  if (paid && insConfirmed && preReady) {
    return "Awaiting Shipment Confirmation";
  }
  return SENDER_PUBLIC_MILESTONE[st] || "In progress";
}

// Returns true if the shipment is in an exception/holding state worth flagging in lists
export function isExceptionState(status) {
  return [
    "DOCUMENTS_REJECTED",
  ].includes(status);
}


export function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    }) + " IST";
  } catch (e) { return iso; }
}
export const NO_GO_RESTRICTED = ["EXPLOSIVES","FIREARMS","NARCOTICS","HAZCHEM","BIOLOGICAL","LIVE_ANIMALS","HUMAN_REMAINS","CASH"];

// Allowed-but-flagged restricted goods — proceed to quote/booking but surface as prominent
// operational Cargo Alerts to staff/rider/captain and repeat on package labels.
export const FLAGGED_GOODS = ["JEWELLERY","PERISHABLE","TEMP_CONTROLLED","LITHIUM_BATTERY","LIQUIDS"];
export const FLAGGED_GOODS_LABELS = {
  JEWELLERY: "Jewellery",
  PERISHABLE: "Perishable",
  TEMP_CONTROLLED: "Temperature-controlled",
  LITHIUM_BATTERY: "Lithium battery",
  LIQUIDS: "Liquids",
};
export function flaggedGoods(declared) {
  if (!Array.isArray(declared)) return [];
  const seen = new Set();
  const out = [];
  for (const g of declared) {
    const code = String(g).trim().toUpperCase();
    if (FLAGGED_GOODS.includes(code) && !seen.has(code)) { seen.add(code); out.push(code); }
  }
  return out;
}
